/**
 * VeriClean Campaign Analytics — client-side tracker
 *
 * Responsibilities:
 *  - Mint and persist a stable visitor_id (localStorage, 1 year) + session_id (sessionStorage)
 *  - Capture first-touch UTM-style params (source, campaign, medium, ref, canvasser, location)
 *    on the first visit, then re-attach them to every event in the session
 *  - Provide track() that POSTs to /api/vericlean/track (fire-and-forget)
 *  - Provide trackPageview() + useVeriCleanTracking() hook used by VeriClean layouts
 *
 * Privacy: only the values listed above are sent. No PII. The backend strips
 * the raw IP and stores only a salted SHA256 hash.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocalItem, setLocalItem } from '@/utils/safeStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const TRACK_URL = `${BACKEND_URL}/api/vericlean/track`;

const VISITOR_KEY = 'vc_analytics_visitor_id';
const SESSION_KEY = 'vc_analytics_session_id';
const ATTR_KEY = 'vc_analytics_attribution';
const LAST_PAGEVIEW_KEY = 'vc_analytics_last_pageview'; // client-side debounce hint
const CLIENT_DEBOUNCE_MS = 5_000; // belt-and-braces; server enforces 30s

const ATTR_FIELDS = ['source', 'campaign', 'medium', 'ref', 'canvasser', 'location'];

function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch { /* fall through */ }
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitorId() {
  try {
    let v = getLocalItem(VISITOR_KEY);
    if (!v) {
      v = uuidv4();
      setLocalItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return null;
  }
}

function getOrCreateSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = uuidv4();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return null;
  }
}

/** Read attribution params from the current URL, persist on first touch. */
function captureAttribution() {
  let existing = {};
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    if (raw) existing = JSON.parse(raw) || {};
  } catch {
    existing = {};
  }

  // Pull from current URL
  let urlAttr = {};
  try {
    const params = new URLSearchParams(window.location.search);
    ATTR_FIELDS.forEach((f) => {
      // accept both `campaign` and `utm_campaign` style
      const v = params.get(f) || params.get(`utm_${f}`);
      if (v && !existing[f]) urlAttr[f] = v.slice(0, 80);
    });
  } catch { /* ignore */ }

  if (Object.keys(urlAttr).length > 0) {
    const merged = { ...existing, ...urlAttr };
    try { sessionStorage.setItem(ATTR_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
    return merged;
  }
  return existing;
}

function readAttribution() {
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    if (raw) return JSON.parse(raw) || {};
  } catch { /* ignore */ }
  return {};
}

function getAccountId() {
  // VeriClean account id may be stored under a known key; never sent if missing.
  try {
    return (
      getLocalItem('vericlean_account_id') ||
      sessionStorage.getItem('vericlean_account_id') ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Fire an analytics event. Fails silently — never blocks the user.
 *
 * @param {string} eventType  e.g. "pageview", "book_started", "job_posted"
 * @param {object} extra      optional overrides: { path, metadata, ... }
 */
export function track(eventType, extra = {}) {
  try {
    const attribution = captureAttribution();
    const path = extra.path || window.location.pathname;

    // Only ever track VeriClean surface area (defence in depth).
    if (typeof path === 'string' && !path.startsWith('/vericlean')) return;

    const body = {
      event_type: eventType,
      path,
      referrer: document.referrer || null,
      visitor_id: getOrCreateVisitorId(),
      session_id: getOrCreateSessionId(),
      account_id: getAccountId(),
      ...ATTR_FIELDS.reduce((acc, f) => {
        if (attribution[f]) acc[f] = attribution[f];
        return acc;
      }, {}),
      // allow per-event override (e.g. explicit campaign on a CTA click)
      ...Object.fromEntries(
        ATTR_FIELDS.filter((f) => extra[f]).map((f) => [f, extra[f]])
      ),
      metadata: extra.metadata || {},
    };

    // Use sendBeacon when available so events survive page navigation
    if (eventType !== 'pageview' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(TRACK_URL, blob);
      return;
    }

    fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch {
    /* never throw */
  }
}

/** Pageview helper — adds a tiny client-side debounce to cut refresh spam. */
export function trackPageview(path) {
  try {
    const p = path || window.location.pathname;
    const key = `${LAST_PAGEVIEW_KEY}:${p}`;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (last && Date.now() - last < CLIENT_DEBOUNCE_MS) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch { /* ignore */ }
  track('pageview');
}

/**
 * React hook — fires a pageview every time the route changes under /vericlean.
 * Drop into the VeriClean layout / pages.
 */
export function useVeriCleanTracking() {
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith('/vericlean')) return;
    // Capture attribution from URL on each route change (first touch wins)
    captureAttribution();
    // Fire the pageview
    trackPageview(location.pathname);

    // If the URL has any campaign params, also emit a `campaign_landing` event
    // the first time we see them in this session.
    try {
      const params = new URLSearchParams(location.search);
      const hasCampaign = ATTR_FIELDS.some(
        (f) => params.get(f) || params.get(`utm_${f}`)
      );
      if (hasCampaign) {
        const key = `vc_analytics_campaign_landed:${location.pathname}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          track('campaign_landing', { path: location.pathname });
        }
      }
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);
}

export const _internals = { uuidv4, getOrCreateVisitorId, readAttribution, captureAttribution };
